/**
 * Katalog (Spec 01): EIN Katalog je Mandant. Speisen/Getraenke/Equipment/Personal in einer
 * Item-Liste (kind). Kind-Filter-Chips + Kategorie-Filter + Suche; Klick auf eine Zeile oeffnet
 * den Bearbeiten-Drawer. "Position anlegen" und "Kategorie" als eigene Drawer.
 *
 * Geld IMMER Integer-Cents (Konvention aus packages/db): das Preisfeld nimmt Euro entgegen und
 * rechnet beim Speichern verlustfrei in Cent (Math.round). Archivieren ist Soft-Delete
 * (archived_at), hartes Loeschen bleibt Inhabern vorbehalten (Backend-Rollen-Gate).
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import type { CatalogCategory, CatalogItem, CatalogKind, MenuCard, PricingUnit, VatClass } from "../ui/types";
import { centsToEuros, money, parseGermanEurosToCents } from "../ui/format";
// LMIV-Hauptallergene + Diät-Flags: EINE gemeinsame Quelle für alle Flächen (ui/lmiv).
import { LMIV, DIETARY, lmivLabel } from "../ui/lmiv";
import { guessPricingUnit } from "@maestro/portionsgroesse-heuristik";
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  InputOptik,
  KeineAngabe,
  LISTEN_KOPF_ZELLE,
  ListFooter,
  LoadingRows,
  PageHeader,
  Select,
  ListenReiter,
  SortierKopf,
  SortierMenue,
  Checkbox,
  Textarea,
} from "../ui/components";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useNarrowViewport } from "../hooks/useNarrowViewport";
import { useContentRefetch } from "../ui/CopilotProvider";

// Design-System-Migration P2 (`catalog.tsx`): Sentinel fuer das Kategorie-Drawer-Select
// "Nur für Art" — dessen echte, waehlbare leere Option ("Alle Arten") Radix' `Select.Item` nicht
// als leerer `value`-String annimmt (dasselbe Muster wie `KEIN_WERT` in `settings.tsx`/
// `event-detail.tsx`).
const KEIN_WERT = "__kein_wert__";

/** Zelle einer Katalog-Zeile (Listenstandard, Brief § 2.3 — gleiche Maße wie die Buchungen-Liste). */
const ZELLE =
  "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";

/** Allergene „ungepflegt“ bzw. „ungeprüft“ (H-02-Rest, Migration 336) — die Zelle zeigt dann „–“. */
function allergeneOffen(it: CatalogItem): boolean {
  return it.allergens == null || (it.allergens.length === 0 && it.allergensCheckedAt == null);
}

/* --------------------------------- Vokabular ------------------------------- */
const KIND_LABEL: Record<CatalogKind, string> = {
  food: "Speise",
  drink: "Getränk",
  equipment: "Equipment",
  staff: "Personal",
  other: "Sonstiges",
};
const KIND_ICON: Record<CatalogKind, string> = {
  food: "restaurant",
  drink: "local_bar",
  equipment: "chair",
  staff: "badge",
  other: "category",
};
const PRICING_UNIT_LABEL: Record<PricingUnit, string> = {
  per_person: "pro Person",
  per_piece: "pro Stück",
  per_hour: "pro Stunde",
  flat: "pauschal",
};
const VAT_LABEL: Record<VatClass, string> = {
  food: "Speise (7 %)",
  drink: "Getränk (19 %)",
  service: "Dienstleistung (19 %)",
};

const KIND_ORDER: CatalogKind[] = ["food", "drink", "equipment", "staff", "other"];
// "Kein Inhalt ohne Heimat" (Konzept 2.1): Ressourcen leben kategorielos in ihrem eigenen Bereich.
const isResourceKind = (k: CatalogKind): boolean => k === "equipment" || k === "staff" || k === "other";

// L10/W6: `centsToEuros` kommt jetzt zentral aus ../ui/format (eine Definition statt Kopien).

/**
 * L1: Sanitisiert die Preis-Eingabe waehrend des Tippens — nur Ziffern, Komma und Punkt
 * (Punkt = Tausender, Komma = Dezimal). Verhindert Freitext wie „12,50abc". Ein zweites Komma
 * wird verworfen, damit hoechstens ein Dezimaltrenner bleibt. Das eigentliche Parsen/Runden
 * passiert weiterhin beim Speichern via parseGermanEurosToCents.
 */
function sanitizePriceInput(value: string): string {
  let out = value.replace(/[^\d.,]/g, "");
  const firstComma = out.indexOf(",");
  if (firstComma !== -1) {
    out = out.slice(0, firstComma + 1) + out.slice(firstComma + 1).replace(/,/g, "");
  }
  return out;
}

/** Form-State fuer Anlegen/Bearbeiten einer Position. */
export interface ItemForm {
  id: string | null;
  name: string;
  kind: CatalogKind;
  categoryId: string;
  priceEuros: string;
  pricingUnit: PricingUnit;
  // STORIA-Bug (08.09.2026): unterscheidet "Default/heuristisch vorgeschlagen" von "vom Nutzer
  // selbst umgestellt" — nur solange dieses Flag false ist, darf ein Name/Beschreibung-Tippen
  // die Einheit noch automatisch nachziehen (nur bei NEUEN Positionen, siehe Aufrufer unten).
  // Bestehende Positionen (formFromItem) gelten immer als „touched" — ein Name-Edit an einer
  // bestehenden Position darf eine schon gespeicherte, ggf. bewusst gewaehlte Einheit nie leise
  // ueberschreiben.
  pricingUnitTouched: boolean;
  vatClass: VatClass;
  description: string;
  priceNote: string;
  // M-K: Tri-State — `null` = ungepflegt (LMIV-relevant, nie still zu „geprüft: keine" [] machen).
  allergens: string[] | null;
  // H-02-Rest (Migration 336): unveraenderte Kopien des beim Oeffnen geladenen Bestands — Diff-
  // Basis fuers Speichern (ein Save OHNE Allergen-Aenderung, z. B. nur ein Preis-Fix, darf einen
  // bestehenden Pruef-Zeitpunkt nicht "gratis" mitziehen) und fuer die Bestaetigen-Aktion bei
  // ungeprueftem Alt-Bestand. Werden NIE durch die Allergen-Editierfelder selbst veraendert.
  allergensLoaded: string[] | null;
  allergensCheckedAt: string | null;
  dietary: Record<string, boolean>;
  // Mehrsprachigkeit: { "<lang>": { name, description } } — im Drawer je Sprache pflegbar.
  translations: Record<string, { name?: string; description?: string | null }>;
  archived: boolean;
}

export function emptyForm(kind: CatalogKind = "food"): ItemForm {
  return {
    id: null,
    name: "",
    kind,
    categoryId: "",
    priceEuros: "",
    pricingUnit: kind === "staff" ? "per_hour" : "per_person",
    pricingUnitTouched: false,
    vatClass: kind === "drink" ? "drink" : kind === "food" ? "food" : "service",
    description: "",
    priceNote: "",
    allergens: null, // neu = ungepflegt (Tri-State), nicht „geprüft: keine"
    allergensLoaded: null,
    allergensCheckedAt: null,
    dietary: {},
    translations: {},
    archived: false,
  };
}

/**
 * STORIA-Bug (Mimmo, 07./08.09.2026): eine NEUE Position bekam bisher immer hart `per_person`
 * (ausser `staff`), unabhaengig davon, ob Name/Beschreibung schon "Platte"/"fuer N Personen"
 * verraten. Bei 40 Gaesten wurde eine als Stueckzahl gemeinte "8" so faelschlich zu "320"
 * hochgerechnet. Wendet die geteilte Heuristik (@maestro/portionsgroesse-heuristik) auf Name+
 * Beschreibung an, aber NUR solange (a) die Position neu ist (kein `id`), (b) die Einheit noch
 * nie vom Nutzer selbst umgestellt wurde (`pricingUnitTouched`), und (c) es keine Personal-
 * Position ist (bleibt immer `per_hour`) — ein Edit an einer BESTEHENDEN Position darf eine
 * schon gespeicherte Einheit nie leise ueberschreiben.
 */
export function mitPortionsgroesseVorschlag(form: ItemForm, patch: Partial<Pick<ItemForm, "name" | "description">>): ItemForm {
  const next = { ...form, ...patch };
  if (next.id != null || next.pricingUnitTouched || next.kind === "staff") return next;
  return { ...next, pricingUnit: guessPricingUnit(next.name, next.description) };
}

/**
 * H-02-Rest (Migration 336): inhaltlicher Vergleich (nicht Referenz) zweier Allergen-Werte —
 * Grundlage dafuer, ob ein Speichern die Allergene UEBERHAUPT beruehrt hat.
 */
function allergensEqual(a: string[] | null, b: string[] | null): boolean {
  if (a == null || b == null) return a === b;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function formFromItem(it: CatalogItem): ItemForm {
  return {
    id: it.id,
    name: it.name,
    kind: it.kind,
    categoryId: it.categoryId ?? "",
    priceEuros: centsToEuros(it.priceCents),
    pricingUnit: it.pricingUnit,
    pricingUnitTouched: true, // bestehende Position: nie automatisch umstellen, siehe ItemForm
    vatClass: it.vatClass,
    description: it.description ?? "",
    priceNote: it.priceNote ?? "",
    allergens: it.allergens ?? null, // `null` (ungepflegt) erhalten, nicht zu [] normalisieren
    allergensLoaded: it.allergens ?? null,
    allergensCheckedAt: it.allergensCheckedAt,
    dietary: it.dietary ?? {},
    translations: (it.translations ?? {}) as Record<string, { name?: string; description?: string | null }>,
    archived: it.archivedAt != null,
  };
}

/** Nachtraegliche KI-Pruefung einer bereits uebernommenen Karte (Vorschlags-Prinzip, editierbar). */
type RecheckStatus = "open" | "saving" | "done" | "error";
type SpellRow = { key: string; catalogItemId: string; field: "name" | "description"; original: string; value: string; status: RecheckStatus; err?: string };
type AllergenRow = { key: string; catalogItemId: string; name: string; current: string[] | null; codes: string[]; reason: string; status: RecheckStatus; err?: string };
// API-Antwort-Formen des recheck-Endpoints.
type ApiSpell = { catalogItemId: string; field: "name" | "description"; original: string; corrected: string };
type ApiAllergen = { catalogItemId: string; name: string; codes: string[]; reason: string; current: string[] | null };

/** Textfeld, das sich an seinen Inhalt anpasst — kein inneres Scrollen beim Editieren. */
function AutoTextarea({ value, disabled, onChange, className }: { value: string; disabled?: boolean; onChange: (v: string) => void; className?: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`resize-none overflow-hidden ${className ?? ""}`}
    />
  );
}

/** Ein Katalog-Vorschlag aus GET /api/catalog/vorschlaege (E-K9). */
interface KatalogVorschlag {
  name: string;
  art: "staff" | "equipment" | "other";
  einheit: "per_person" | "per_piece" | "per_hour" | "flat";
  preisCents: number;
  beleg: string;
}

const EINHEIT_LABEL: Record<KatalogVorschlag["einheit"], string> = {
  per_person: "p. P.",
  per_piece: "je Stk",
  per_hour: "je Std",
  flat: "pauschal",
};

/**
 * E-K9 (Antoine, 05.08.2026: „Zukünftig soll MAESTRO das automatisch machen und im Tool
 * vorschlagen") — MAESTRO findet bepreiste Zusatzleistungen in den Referenzunterlagen und
 * schlägt sie HIER zur Übernahme vor. Die Bestätigung bleibt beim Betreiber (E-E9): erst der
 * Klick macht aus dem Beleg eine verkaufbare Position. Bereits vorhandene Namen filtert der
 * Server heraus — ist nichts offen, verschwindet die Kachel von selbst.
 */
function VorschlaegeBanner({ onUebernommen }: { onUebernommen: () => void }) {
  const { data, loading, reload } = useApi<{ vorschlaege: KatalogVorschlag[]; ausBelegen: number }>("/api/catalog/vorschlaege");
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const vorschlaege = data?.vorschlaege ?? [];
  if (loading || vorschlaege.length === 0) return null;

  const uebernehmen = async (positionen: KatalogVorschlag[], key: string) => {
    setBusy(key);
    setFehler(null);
    try {
      await apiMutate(`/api/catalog/vorschlaege/uebernehmen`, "POST", { positionen });
      reload();
      onUebernommen();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Übernahme fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="mb-4 overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline-variant/30 bg-surface-container-low px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-semibold text-on-background">
          <Icon name="auto_awesome" className="text-[18px] text-primary" />
          {vorschlaege.length} Vorschläge aus Ihren Referenzunterlagen
        </span>
        <Button
          type="button"
          variant="secondary"
          className="!py-1.5 text-sm"
          disabled={busy != null}
          onClick={() => void uebernehmen(vorschlaege, "__alle__")}
        >
          {busy === "__alle__" ? "Übernimmt …" : "Alle übernehmen"}
        </Button>
      </div>
      <ul className="divide-y divide-outline-variant/20">
        {vorschlaege.map((v) => (
          <li key={v.name} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
            <div className="min-w-0">
              <span className="text-sm font-medium text-on-background">{v.name}</span>
              <span className="ml-2 text-sm text-on-surface-variant">
                {(v.preisCents / 100).toFixed(2).replace(".", ",")} € {EINHEIT_LABEL[v.einheit]}
              </span>
              <div className="text-xs text-on-surface-variant" title="Fundstelle in Ihren Referenzunterlagen">
                Beleg: „{v.beleg}"
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              className="!py-1 text-sm"
              disabled={busy != null}
              onClick={() => void uebernehmen([v], v.name)}
            >
              {busy === v.name ? "…" : "Übernehmen"}
            </Button>
          </li>
        ))}
      </ul>
      {fehler && <div className="border-t border-outline-variant/20 px-4 py-2 text-sm text-error">{fehler}</div>}
    </Card>
  );
}

export default function CatalogPage() {
  const itemsQ = useApi<CatalogItem[]>("/api/catalog/items?includeArchived=true");
  const catsQ = useApi<CatalogCategory[]>("/api/catalog/categories?includeArchived=true");
  // P1 (Speisekarten): Karten fuer den Filter — Kategorien tragen cardId, Positionen haengen
  // ueber ihre Kategorie an der Karte.
  const cardsQ = useApi<MenuCard[]>("/api/menu-cards");

  // A2c: Schreibaktionen des Copilot (z. B. commit_card -> changed.resource === "catalog") lösen
  // hier gezielt einen Refetch der Katalog-Listen aus — nur diese Seite, kein globaler Reload.
  useContentRefetch("catalog", () => {
    itemsQ.reload();
    catsQ.reload();
    cardsQ.reload();
  });

  const [kindFilter, setKindFilter] = useState<CatalogKind | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  // Handy-Mockup 26.09.2026: unter 640 px Karten statt Tabelle; Kategorie + Archiv hinter „Filter“.
  const narrow = useNarrowViewport();
  const [filterOffen, setFilterOffen] = useState(false);
  // Nachtraegliche KI-Pruefung (Rechtschreibung + Allergene) einer bereits uebernommenen Karte.
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckBusy, setRecheckBusy] = useState(false);
  const [recheckAllergenBusy, setRecheckAllergenBusy] = useState(false);
  const [recheckDone, setRecheckDone] = useState(false);
  const [recheckErr, setRecheckErr] = useState<string | null>(null);
  const [recheckSpellRows, setRecheckSpellRows] = useState<SpellRow[]>([]);
  const [recheckAllergenRows, setRecheckAllergenRows] = useState<AllergenRow[]>([]);
  const [recheckIncludeDeclared, setRecheckIncludeDeclared] = useState(false);
  const [recheckDirty, setRecheckDirty] = useState(false);
  // CX-Audit 2026-07-17: ?source=ai_import zeigt gezielt die KI-importierten Positionen —
  // Ziel des „Fertig"-Schritts im Speisekarten-Import (vorher landete man unfokussiert in
  // der Gesamtliste). Abwählbar über einen sichtbaren Filter-Chip.
  const [params, setParams] = useSearchParams();
  const sourceFilter = params.get("source");
  // Pillar-Einstieg (UX 19.07.2026): /katalog zeigt ZUERST die Speisekarten als Kacheln —
  // der Mensch waehlt eine Karte, dann kommt die Liste. Der Karten-Filter lebt deshalb in
  // der URL (?cardId=<uuid|none>), ?view=liste ist die bewusste Gesamtliste; Deep-Links
  // (?source=ai_import) springen weiterhin direkt in die Liste.
  const cardFilter = params.get("cardId") ?? "all";
  const clearSourceFilter = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("source");
        return next;
      },
      { replace: true },
    );

  const [itemForm, setItemForm] = useState<ItemForm | null>(null);
  // Mehrsprachigkeit: aktuell bearbeitete Übersetzungs-Sprache im Positions-Drawer.
  const [editLang, setEditLang] = useState("en");
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  // Design-System-Migration P2 (s. Kopfkommentar `KEIN_WERT`): die beiden Selects des Kategorie-
  // Drawers lebten bisher im FormData-basierten `<form onSubmit>` — Radix' `Select` erzeugt kein
  // natives, FormData-taugliches Element, daher lokaler React-State + `useEffect`-Reset bei jedem
  // Oeffnen (analog `settings.tsx`/`event-detail.tsx`), statt weiterhin per `form.get(...)`
  // ausgelesen zu werden (s. `handleCreateCategory`).
  const [catCardId, setCatCardId] = useState("");
  const [catKindScope, setCatKindScope] = useState(KEIN_WERT);
  useEffect(() => {
    if (catDrawerOpen) {
      setCatCardId("");
      setCatKindScope(KEIN_WERT);
    }
  }, [catDrawerOpen]);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const items = itemsQ.data ?? [];
  const categories = useMemo(
    () => (catsQ.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [catsQ.data],
  );
  const activeCategories = categories.filter((c) => c.archivedAt == null);
  // food/drink nur in Kategorien MIT Speisekarte (Konzept 2.1); diese Menge speist den Item-Drawer.
  const cardedCategories = activeCategories.filter((c) => (c.cardId ?? null) != null);
  const categoryName = (id: string | null) => (id ? categories.find((c) => c.id === id)?.name ?? "—" : "Ohne Kategorie");
  const cards = cardsQ.data ?? [];
  // Kategorie -> Karte (P1): Positionen gehoeren ueber ihre Kategorie zu einer Karte.
  const cardByCategory = useMemo(() => new Map(categories.map((c) => [c.id, c.cardId ?? null])), [categories]);
  const cardNameById = useMemo(() => new Map(cards.map((c) => [c.id, c.name])), [cards]);
  const matchesCard = (it: CatalogItem): boolean => {
    if (cardFilter === "all") return true;
    const cardId = it.categoryId ? cardByCategory.get(it.categoryId) ?? null : null;
    return cardFilter === "none" ? cardId == null : cardId === cardFilter;
  };
  // Bei gewaehlter Karte zeigt der Kategorien-Filter nur deren Kategorien (weniger Rauschen).
  const categoryOptions = activeCategories.filter((c) =>
    cardFilter === "all" ? true : cardFilter === "none" ? (c.cardId ?? null) == null : c.cardId === cardFilter,
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => (showArchived ? true : it.archivedAt == null))
      .filter((it) => (sourceFilter ? it.source === sourceFilter : true))
      .filter((it) => (kindFilter === "all" ? true : it.kind === kindFilter))
      .filter((it) => matchesCard(it))
      .filter((it) =>
        categoryFilter === "all"
          ? true
          : categoryFilter === "none"
            ? it.categoryId == null
            : it.categoryId === categoryFilter,
      )
      .filter((it) => (q ? it.name.toLowerCase().includes(q) || (it.description ?? "").toLowerCase().includes(q) : true))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, kindFilter, categoryFilter, cardFilter, cardByCategory, showArchived, sourceFilter]);

  // Sichtbare Karten-Gruppierung (UX 19.07.2026): „Alle Karten" wirkte wie ein flacher
  // Eintopf — die Speisekarte ist aber die natuerliche Gliederung des Katalogs. Die Liste
  // gruppiert deshalb nach Karte (Reihenfolge der Karten-Verwaltung), „Ohne Karte" zuletzt.
  // Ohne angelegte Karten bleibt alles wie zuvor (eine Gruppe ohne Header).
  // Listenstandard (Punkt 95): sortiert wird INNERHALB jeder Karten-Gruppe — die Zwischenzeilen
  // bleiben, eine Sortierung zieht keine Position in eine fremde Karte. Ohne Sortierung gilt die
  // bisherige Reihenfolge (sortOrder, dann Name).
  const spalten = useMemo<SortSpalte<CatalogItem>[]>(
    () => [
      { key: "position", label: "Position", type: "text", accessor: (it) => it.name },
      // „Ohne Kategorie“ ist ein Platzhalter und zählt als Leerwert (steht immer am Ende).
      { key: "kategorie", label: "Kategorie", type: "text", accessor: (it) => (it.categoryId ? categories.find((c) => c.id === it.categoryId)?.name : null) },
      // Anzahl der Allergene: „keine“ = 0 zuerst, ungepflegt/ungeprüft („–“) immer am Ende.
      { key: "allergene", label: "Allergene", type: "number", accessor: (it) => (allergeneOffen(it) ? null : it.allergens!.length) },
      // Ein Preishinweis ersetzt in der Zelle den Betrag — dann gibt es keinen sortierbaren Betrag.
      { key: "preis", label: "Preis", type: "money", accessor: (it) => (it.priceNote ? null : it.priceCents) },
    ],
    [categories],
  );
  const sort = useListSort("katalog", spalten);

  const groupedRows = useMemo((): { key: string; label: string | null; items: CatalogItem[] }[] => {
    if (cards.length === 0) return [{ key: "all", label: null, items: filtered }];
    const byCard = new Map<string | null, CatalogItem[]>();
    for (const it of filtered) {
      const cardId = it.categoryId ? cardByCategory.get(it.categoryId) ?? null : null;
      const list = byCard.get(cardId) ?? [];
      list.push(it);
      byCard.set(cardId, list);
    }
    const groups: { key: string; label: string | null; items: CatalogItem[] }[] = [];
    for (const k of cards) {
      const inCard = byCard.get(k.id) ?? [];
      if (inCard.length > 0) groups.push({ key: k.id, label: k.name, items: inCard });
    }
    const none = byCard.get(null) ?? [];
    if (none.length > 0) groups.push({ key: "none", label: "Ohne Karte", items: none });
    return groups;
  }, [filtered, cards, cardByCategory]);

  // L2: Zaehler beziehen sich auf die GEFILTERTE Menge (Textsuche + Kategorie + Archiv-Toggle),
  // nur der Kind-Filter selbst wird ausgeklammert — jeder Chip zeigt seine eigene Kind-Zahl.
  // Sonst zeigten die Tabs „Alle (11)" obwohl die Suche nur 5 Zeilen listet.
  const kindCounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = items
      .filter((it) => (showArchived ? true : it.archivedAt == null))
      .filter((it) => matchesCard(it))
      .filter((it) =>
        categoryFilter === "all"
          ? true
          : categoryFilter === "none"
            ? it.categoryId == null
            : it.categoryId === categoryFilter,
      )
      .filter((it) => (q ? it.name.toLowerCase().includes(q) || (it.description ?? "").toLowerCase().includes(q) : true));
    const counts: Record<string, number> = { all: base.length };
    for (const k of KIND_ORDER) counts[k] = base.filter((it) => it.kind === k).length;
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, categoryFilter, cardFilter, cardByCategory, showArchived]);

  function openCreate() {
    setFormError(null);
    setItemForm(emptyForm(kindFilter === "all" ? "food" : kindFilter));
  }
  function openEdit(it: CatalogItem) {
    setFormError(null);
    setItemForm(formFromItem(it));
  }

  async function handleSaveItem(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!itemForm) return;
    // N-F2: leeres Feld = 0 (erlaubt), sonst ungültige Eingabe als Fehler melden statt still auf 0.
    const priceRaw = itemForm.priceEuros.trim();
    const priceCents = priceRaw === "" ? 0 : parseGermanEurosToCents(priceRaw);
    if (priceCents == null) {
      setFormError("Ungültiger Preis – bitte als Betrag eingeben, z. B. 12,90.");
      return;
    }
    // Heimat-Regel clientseitig spiegeln (der Server erzwingt sie ohnehin).
    if (!isResourceKind(itemForm.kind) && !itemForm.categoryId) {
      setFormError("Speisen und Getränke brauchen eine Kategorie, die zu einer Speisekarte gehört.");
      return;
    }
    setBusy(true);
    setFormError(null);
    // H-02-Rest (Migration 336): beim Bearbeiten `allergens` NUR mitschicken, wenn es sich
    // tatsaechlich geaendert hat. Der Server setzt den Pruef-Zeitpunkt IMMER, sobald `allergens`
    // im PATCH-Body steht (routes/catalog.ts, withAllergensChecked) — ohne diesen Guard wuerde
    // JEDES Speichern (auch ein reiner Preis-/Namens-Fix, das Formular sendet immer den ganzen
    // Zustand) einen ungeprueften Alt-Bestand "gratis" als bestaetigt markieren. Beim Neuanlegen
    // (kein itemForm.id) gibt es keinen Bestand zum Vergleichen -> immer mitschicken.
    const allergensChanged = itemForm.id == null || !allergensEqual(itemForm.allergens, itemForm.allergensLoaded);
    const payload = {
      name: itemForm.name.trim(),
      kind: itemForm.kind,
      categoryId: isResourceKind(itemForm.kind) ? null : itemForm.categoryId || null,
      priceCents,
      pricingUnit: itemForm.pricingUnit,
      vatClass: itemForm.vatClass,
      description: itemForm.description.trim() || null,
      priceNote: itemForm.priceNote.trim() || null,
      ...(allergensChanged ? { allergens: itemForm.allergens } : {}),
      dietary: itemForm.dietary,
      // Mehrsprachigkeit: leere Übersetzungen (kein Name) fallen beim Speichern raus.
      translations: Object.fromEntries(Object.entries(itemForm.translations).filter(([, v]) => v?.name?.trim())),
    };
    try {
      if (itemForm.id) {
        await apiMutate(`/api/catalog/items/${itemForm.id}`, "PATCH", { ...payload, archived: itemForm.archived });
      } else {
        await apiMutate("/api/catalog/items", "POST", payload);
      }
      setItemForm(null);
      itemsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  /** Punkt 129: Kopie „<Name> (Kopie)“ (freier Name vergibt der Server), danach direkt zum Bearbeiten öffnen. */
  async function duplicateItem(id: string) {
    setBusy(true);
    try {
      const copy = await apiMutate<CatalogItem>(`/api/catalog/items/${id}/duplicate`, "POST", {});
      itemsQ.reload();
      openEdit(copy);
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive(it: CatalogItem) {
    try {
      await apiMutate(`/api/catalog/items/${it.id}`, "PATCH", { archived: it.archivedAt == null });
      itemsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    }
  }

  /** Allergen-Vorschläge (neu) laden. include=true bewertet AUCH bereits erfasste Positionen. */
  async function loadRecheckAllergens(include: boolean, errsAcc?: string[]) {
    try {
      const a = await apiMutate<{ suggestions?: ApiAllergen[] }>(`/api/menu-cards/${cardFilter}/recheck`, "POST", { step: "allergens", includeDeclared: include });
      setRecheckAllergenRows(
        (a.suggestions ?? []).map((s) => ({ key: s.catalogItemId, catalogItemId: s.catalogItemId, name: s.name, current: s.current, codes: s.codes, reason: s.reason, status: "open" as const })),
      );
    } catch (e) {
      const msg = `Allergene: ${errorText(e)}`;
      if (errsAcc) errsAcc.push(msg);
      else setRecheckErr(msg);
    }
  }

  /** Karte nachtraeglich von der KI pruefen lassen (Rechtschreibung + Allergene). Aendert nichts. */
  async function runRecheck() {
    if (cardFilter === "all" || cardFilter === "none") return;
    setRecheckOpen(true);
    setRecheckBusy(true);
    setRecheckDone(false);
    setRecheckErr(null);
    setRecheckSpellRows([]);
    setRecheckAllergenRows([]);
    const errs: string[] = [];
    try {
      const s = await apiMutate<{ corrections?: ApiSpell[] }>(`/api/menu-cards/${cardFilter}/recheck`, "POST", { step: "spell" });
      setRecheckSpellRows(
        (s.corrections ?? []).map((c) => ({ key: `${c.catalogItemId}:${c.field}`, catalogItemId: c.catalogItemId, field: c.field, original: c.original, value: c.corrected, status: "open" as const })),
      );
    } catch (e) {
      errs.push(`Rechtschreibung: ${errorText(e)}`);
    }
    await loadRecheckAllergens(recheckIncludeDeclared, errs);
    setRecheckErr(errs.length ? errs.join(" · ") : null);
    setRecheckDone(true);
    setRecheckBusy(false);
  }

  /** Nur die Allergen-Prüfung neu anstoßen (mit/ohne bereits erfasste Positionen). */
  async function rerunRecheckAllergens(include: boolean) {
    if (cardFilter === "all" || cardFilter === "none") return;
    setRecheckIncludeDeclared(include);
    setRecheckAllergenBusy(true);
    setRecheckErr(null);
    await loadRecheckAllergens(include);
    setRecheckDone(true);
    setRecheckAllergenBusy(false);
  }

  function setSpellRowValue(key: string, value: string) {
    setRecheckSpellRows((rows) => rows.map((r) => (r.key === key ? { ...r, value, status: r.status === "error" ? "open" : r.status } : r)));
  }
  function toggleAllergenRowCode(key: string, code: string) {
    setRecheckAllergenRows((rows) =>
      rows.map((r) => (r.key === key ? { ...r, codes: r.codes.includes(code) ? r.codes.filter((x) => x !== code) : [...r.codes, code], status: r.status === "error" ? "open" : r.status } : r)),
    );
  }

  /** Eine Schreibkorrektur (evtl. manuell geändert) anwenden — gleiche ID, Paket-Links bleiben. */
  async function applySpellRow(row: SpellRow) {
    if (row.status === "saving" || row.status === "done") return;
    const value = row.value.trim();
    if (!value) {
      setRecheckSpellRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "error", err: "Text darf nicht leer sein." } : r)));
      return;
    }
    setRecheckSpellRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "saving", err: undefined } : r)));
    try {
      await apiMutate(`/api/catalog/items/${row.catalogItemId}`, "PATCH", row.field === "name" ? { name: value } : { description: value });
      setRecheckSpellRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, value, status: "done" } : r)));
      setRecheckDirty(true);
    } catch (e) {
      const msg = errorText(e);
      setRecheckSpellRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "error", err: msg } : r)));
    }
  }
  /** Einen Allergen-Vorschlag (evtl. manuell angepasst) bestätigen. */
  async function applyAllergenRow(row: AllergenRow) {
    if (row.status === "saving" || row.status === "done") return;
    setRecheckAllergenRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "saving", err: undefined } : r)));
    try {
      await apiMutate(`/api/catalog/items/${row.catalogItemId}`, "PATCH", { allergens: row.codes });
      setRecheckAllergenRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "done" } : r)));
      setRecheckDirty(true);
    } catch (e) {
      const msg = errorText(e);
      setRecheckAllergenRows((rows) => rows.map((r) => (r.key === row.key ? { ...r, status: "error", err: msg } : r)));
    }
  }
  async function applyAllSpellRows() {
    for (const r of recheckSpellRows) if (r.status === "open" || r.status === "error") await applySpellRow(r);
  }
  async function applyAllAllergenRows() {
    for (const r of recheckAllergenRows) if (r.status === "open" || r.status === "error") await applyAllergenRow(r);
  }
  /** Panel schließen — geänderte Karte einmal frisch laden (nicht nach jedem Klick). */
  function closeRecheck() {
    setRecheckOpen(false);
    if (recheckDirty) {
      itemsQ.reload();
      setRecheckDirty(false);
    }
  }

  async function handleCreateCategory(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = (form.get("name") as string)?.trim();
    if (!name) return;
    // Design-System-Migration P2: `cardId`/`kindScope` kommen seit dem Umstieg der beiden
    // Selects auf `<Select>` (Radix, s. Kopfkommentar `KEIN_WERT`) aus lokalem React-State statt
    // aus `new FormData(e.currentTarget)` — Radix' Select erzeugt kein natives, FormData-taugliches
    // Element. Das Namensfeld bleibt unveraendert per FormData ausgelesen (echtes `<input>`).
    const kindScope = catKindScope === KEIN_WERT ? "" : catKindScope;
    const cardId = catCardId;
    if (!cardId) {
      setFormError("Bitte eine Speisekarte wählen – jede Kategorie gehört zu einer Karte.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/catalog/categories", "POST", {
        name,
        cardId,
        kindScope: kindScope || null,
      });
      setCatDrawerOpen(false);
      catsQ.reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  // P1 (§ 4.2): die Positionsliste heisst wie die Karte, die sie zeigt — "Katalog" stand vorher
  // ueber drei verschiedenen Ansichten und sagte damit nichts (§ 2.2).
  const listenTitel =
    cardFilter === "all"
      ? "Alle Positionen"
      : cardFilter === "none"
        ? "Ohne Karte"
        : cards.find((k) => k.id === cardFilter)?.name ?? "Karte";

  const loading = itemsQ.loading || catsQ.loading;
  const error = itemsQ.error ?? catsQ.error;

  return CatalogInner();

  /* ----------------------- Einstieg: die Kartenliste --------------------------- */
  // P1 (docs/KONZEPT-KATALOG-NAVIGATION.md § 4.2): /katalog war ZWEI Seiten in einer — eine
  // Kachelwand aller Karten und die Positionsliste einer Karte, beide „Katalog" betitelt, und
  // /katalog/karten war eine DRITTE Kartenansicht. Der Betreiber sah Karten an drei Orten und
  // fand das Archivieren an keinem (§ 1, § 2.2). Die Kachelwand entfällt deshalb; /katalog/karten
  // ist DIE Kartenansicht, und /katalog ohne Karten-/Listen-/Quellen-Parameter leitet dorthin.
  // Alte Links (?cardId=…, ?view=liste, ?source=ai_import) bleiben gültig (§ 7).
  function CatalogInner() {
    const zeigtListe = params.get("cardId") != null || params.get("view") === "liste" || sourceFilter != null;
    if (!zeigtListe) return <Navigate to="/katalog/karten" replace />;
    return renderList();
  }

  function renderList() {
  // Fußzeile „{n} von {gesamt} Positionen“: gesamt = diese Karte/Ansicht (inkl. Archiv-Schalter),
  // ohne Suche, Art-, Kategorie- und Quellen-Filter.
  const gesamtInAnsicht = items.filter((it) => (showArchived || it.archivedAt == null) && matchesCard(it)).length;
  return (
    <>
      <PageHeader
        title={listenTitel}
        zurueck={{ label: "Karten", to: "/katalog/karten" }}
        subtitle="Speisen, Getränke, Equipment und Personal – die Bausteine Ihrer Angebote."
        actions={
          <>
            {/* P2 (§ 4.3): der Kopf traegt NUR Aktionen dieser Seite. Vorher standen hier sechs
                Knoepfe in gleicher Optik nebeneinander, drei davon navigierten (Karten, Pakete,
                Karte importieren) und drei handelten — gleiche Optik, gegensaetzliche Bedeutung
                (§ 2.3). Die drei Wege stehen jetzt in der Seitenleiste bzw. auf /katalog/karten. */}
            {cardFilter !== "all" && cardFilter !== "none" && (
              <Button type="button" variant="secondary" onClick={() => void runRecheck()} title="Rechtschreibung & Allergene dieser Karte erneut von der KI prüfen lassen">
                <Icon name="spellcheck" className="text-[18px]" /> Karte prüfen (KI)
                {/* § 4.3: der einzige Knopf hier, der Modellkosten ausloest — das muss man SEHEN,
                    nicht erst im Tooltip finden, sonst unterscheidet er sich nicht von den
                    kostenlosen Nachbarn. */}
                <span className="ml-1 rounded-full bg-secondary/15 px-1.5 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:text-[#b8ae9e]">
                  kostet KI-Aufrufe
                </span>
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={() => setCatDrawerOpen(true)}>
              <Icon name="new_label" className="text-[18px]" /> Kategorie
            </Button>
            <Button type="button" variant="primary" onClick={openCreate}>
              <Icon name="add" className="text-[18px]" /> Position anlegen
            </Button>
          </>
        }
      />

      {/* P1 (§ 4.2): der Breadcrumb ist der EINZIGE Ort, der den Kartenkontext noch fuehrt — seit
          Punkt 115 als `zurueck` im PageHeader (gemeinsamer Rueckweg ueber dem Titel). */}
      {/* E-K9: die Vorschlags-Kachel hing an der entfallenen Kachelwand und steht jetzt ueber der
          Positionsliste — dort, wo die Positionen entstehen, die sie vorschlaegt. */}
      <VorschlaegeBanner onUebernommen={() => { itemsQ.reload(); catsQ.reload(); cardsQ.reload(); }} />

      {/* Kind-Filter-Chips; auf dem Handy eine Reiterleiste statt drei Zeilen Chips. */}
      {narrow ? (
        <div className="mb-3">
          <ListenReiter
            label="Positionen nach Art"
            reiter={[
              { key: "all" as const, label: "Alle", anzahl: kindCounts.all ?? 0 },
              ...KIND_ORDER.map((k) => ({ key: k, label: KIND_LABEL[k], anzahl: kindCounts[k] ?? 0 })),
            ]}
            aktiv={kindFilter}
            onWahl={setKindFilter}
          />
        </div>
      ) : (
      <div className="mb-4 flex flex-wrap gap-2">
        <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} label={`Alle (${kindCounts.all ?? 0})`} />
        {KIND_ORDER.map((k) => (
          <FilterChip
            key={k}
            active={kindFilter === k}
            onClick={() => setKindFilter(k)}
            icon={KIND_ICON[k]}
            label={`${KIND_LABEL[k]} (${kindCounts[k] ?? 0})`}
          />
        ))}
      </div>
      )}

      <Card className="overflow-hidden">
        {/* Toolbar: Suche + Kategorie-Filter + Archiv-Toggle */}
        <div className="flex flex-wrap items-center gap-3 border-b border-outline-variant/30 p-4 dark:border-outline/30">
          <div className="group relative min-w-[220px] flex-1 max-w-sm">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
            />
            <Input
              className="!pl-10"
              placeholder="Suchen (Name, Beschreibung) …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {narrow && (
            <>
              <SortierMenue optionen={KATALOG_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} className="[&>div]:min-w-[120px]" />
              <Button type="button" variant="secondary" className="!px-2.5" aria-expanded={filterOffen} aria-label="Filter" onClick={() => setFilterOffen((o) => !o)}>
                <Icon name="tune" className="text-[18px]" />
              </Button>
            </>
          )}
          {(!narrow || filterOffen) && (
          <>
          <div className="max-w-[220px]">
            <Select
              value={categoryFilter}
              onValueChange={setCategoryFilter}
              options={[
                { value: "all", label: "Alle Kategorien" },
                { value: "none", label: "Ohne Kategorie" },
                ...categoryOptions.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
            <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Archivierte zeigen
          </label>
          </>
          )}
          {/* CX-Audit 2026-07-17: sichtbarer, abwählbarer Chip für den ?source=-Deep-Link
              (Ziel des „Fertig"-Schritts im Speisekarten-Import). */}
          {sourceFilter && (
            <button
              type="button"
              onClick={clearSourceFilter}
              title="Filter entfernen"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-label-md text-[11.5px] font-semibold text-primary hover:bg-primary/20"
            >
              {sourceFilter === "ai_import" ? "Nur KI-Import" : `Quelle: ${sourceFilter}`}
              <Icon name="close" className="text-[14px]" />
            </button>
          )}
        </div>

        {formError && !itemForm && !catDrawerOpen && <ErrorNote message={formError} />}
        {error ? (
          <ErrorNote zentriert title="Positionen konnten nicht geladen werden" message={error} onRetry={() => { itemsQ.reload(); catsQ.reload(); }} />
        ) : !loading && gesamtInAnsicht === 0 ? (
          <EmptyState
            icon="menu_book"
            title="Noch keine Positionen"
            hint="Legen Sie Ihre erste Katalog-Position an – oder importieren Sie eine Speisekarte."
          />
        ) : !loading && filtered.length === 0 ? (
          <EmptyState
            icon="menu_book"
            title="Keine Positionen gefunden"
            hint="Filter oder Suche anpassen."
            link={
              search || kindFilter !== "all" || categoryFilter !== "all"
                ? {
                    label: "Alle Positionen anzeigen",
                    onClick: () => {
                      setSearch("");
                      setKindFilter("all");
                      setCategoryFilter("all");
                    },
                  }
                : undefined
            }
          />
        ) : narrow ? (
          <KatalogKarten
            gruppen={groupedRows.map((g) => ({ ...g, items: sort.sortiere(g.items) }))}
            loading={loading}
            categoryName={categoryName}
            onOpen={openEdit}
          />
        ) : (
          // Listenstandard (Brief § 2.4): eigener Scrollbereich, damit der Kopf `sticky` stehen
          // bleibt (an der Karte mit `overflow-hidden` scheitert `sticky` still).
          // ponytail: keine Virtualisierung (Plan § Grundbefund); nachrüsten ab ~200 Zeilen je Ansicht.
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
            <table className="w-full min-w-[760px] table-fixed border-collapse text-left text-[13px]">
              <colgroup>
                <col />
                <col className="w-[190px]" />
                <col className="w-[200px]" />
                <col className="w-[150px]" />
                <col className="w-[64px]" />
              </colgroup>
              <thead>
                <tr>
                  {spalten.map((sp) => (
                    <SortierKopf
                      key={sp.key}
                      label={sp.label}
                      aktiv={sort.sortKey === sp.key ? sort.dir : null}
                      onClick={() => sort.toggle(sp.key)}
                      rechts={sp.key === "preis"}
                    />
                  ))}
                  <th className={LISTEN_KOPF_ZELLE}>
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              {loading ? (
                <LoadingRows rows={8} spalten={["text", "text", "text", "text", "leer"]} />
              ) : (
              <tbody>
                {groupedRows.map((group) => (
                  <Fragment key={group.key}>
                    {group.label != null && (
                      /* Sichtbare Karten-Gruppierung: die Speisekarte IST die Gliederung des
                         Katalogs — nicht nur ein Filter. „Ohne Karte" bildet die letzte Gruppe. */
                      <tr className="bg-[#FBF8F1]/70 dark:bg-white/[0.03]" data-testid="katalog-gruppe">
                        <td colSpan={5} className="border-t border-[#F6F0E4] px-[18px] py-2.5 dark:border-white/[0.06]">
                          <span className="inline-flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                            <Icon name="menu_book" className="text-[16px] text-primary" /> {group.label}
                            <span className="font-body-sm text-body-sm normal-case tracking-normal text-on-surface-variant/70">
                              · {group.items.length} Position{group.items.length === 1 ? "" : "en"}
                            </span>
                          </span>
                        </td>
                      </tr>
                    )}
                    {sort.sortiere(group.items).map((it) => (
                  <tr
                    key={it.id}
                    tabIndex={0}
                    className={`group cursor-pointer transition-colors hover:bg-[#FBF6EC] focus-visible:bg-[#FBF6EC] focus-visible:shadow-[inset_3px_0_0_#C2410C] focus-visible:outline-none dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03] ${
                      it.archivedAt != null ? "opacity-55" : ""
                    }`}
                    onClick={() => openEdit(it)}
                    onKeyDown={(e) => {
                      // Brief § 2.3: Enter/Leertaste öffnet — nur auf der Zeile selbst, nicht auf
                      // den Knöpfen/Links darin (die lösen ihre eigene Handlung aus).
                      if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        openEdit(it);
                      }
                    }}
                  >
                    <td className={ZELLE}>
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-container text-on-secondary-container dark:bg-surface-variant dark:text-inverse-on-surface">
                          <Icon name={KIND_ICON[it.kind]} className="text-[18px]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-2">
                            <span title={it.name} className="min-w-0 truncate font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface">
                              {it.name}
                            </span>
                            {it.archivedAt != null && (
                              <span className="rounded bg-surface-container-high px-1.5 py-0.5 font-label-md text-[10px] uppercase tracking-wide text-on-surface-variant dark:bg-white/[0.08]">
                                Archiviert
                              </span>
                            )}
                            {it.source === "ai_import" && (
                              /* CX-Audit 2026-07-17: Badge verlinkt zur Import-Seite (Herkunft nachvollziehen /
                                 nächste Karte importieren) — stopPropagation, da die Zeile den Edit-Drawer öffnet. */
                              <Link
                                to="/katalog/import"
                                onClick={(e) => e.stopPropagation()}
                                className="rounded bg-primary/10 px-1.5 py-0.5 font-label-md text-[10px] uppercase tracking-wide text-primary hover:bg-primary/20"
                                title="Per KI importiert — zur Import-Seite"
                              >
                                KI
                              </Link>
                            )}
                          </div>
                          {it.description && (
                            <div className="truncate max-w-[280px] font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                              {it.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={`${ZELLE} text-on-surface-variant dark:text-secondary-fixed-dim`}>
                      {it.categoryId ? (
                        <span className="block truncate" title={categoryName(it.categoryId)}>
                          {categoryName(it.categoryId)}
                        </span>
                      ) : (
                        // Leerwert (Brief § 2.3) — der Text bleibt, damit „Ohne Kategorie“ lesbar ist.
                        <span className="text-[#9A9080]">Ohne Kategorie</span>
                      )}
                    </td>
                    <td className={ZELLE}>
                      {/* H-02-Rest (Migration 336): leeres Array OHNE allergensCheckedAt ist
                          ungeprüfter Alt-Bestand (Import vor der Einzel-Bestätigung) — optisch wie
                          „ungepflegt" behandelt (NIE „keine"), eigener Tooltip erklärt den Grund. */}
                      {it.allergens == null || (it.allergens.length === 0 && it.allergensCheckedAt == null) ? (
                        <span
                          role="img"
                          aria-label="keine Angabe"
                          className="text-[#CFC5B3] dark:text-white/25"
                          title={
                            it.allergens != null
                              ? "Allergene ungeprüft – Alt-Bestand aus einem Import vor der Einzel-Bestätigung. Bitte prüfen und bestätigen."
                              : "Allergene ungepflegt"
                          }
                        >
                          –
                        </span>
                      ) : it.allergens.length === 0 ? (
                        <span className="font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">keine</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {it.allergens.map((a) => (
                            <AllergenKuerzel key={a} code={a} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className={`${ZELLE} text-right`}>
                      <div className="font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">
                        {it.priceNote ? it.priceNote : money(it.priceCents)}
                      </div>
                      <div className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {PRICING_UNIT_LABEL[it.pricingUnit]}
                      </div>
                    </td>
                    <td className={`${ZELLE} whitespace-nowrap text-right`}>
                      <Button
                        type="button"
                        variant="ghost"
                        className="!px-2 opacity-0 transition-opacity group-hover:opacity-100"
                        title="Duplizieren"
                        aria-label="Duplizieren"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void duplicateItem(it.id);
                        }}
                      >
                        <Icon name="content_copy" className="text-[18px]" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="!px-2 opacity-0 transition-opacity group-hover:opacity-100"
                        title={it.archivedAt != null ? "Wiederherstellen" : "Archivieren"}
                        onClick={(e) => {
                          e.stopPropagation();
                          void toggleArchive(it);
                        }}
                      >
                        <Icon name={it.archivedAt != null ? "unarchive" : "archive"} className="text-[18px]" />
                      </Button>
                    </td>
                  </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
              )}
            </table>
          </div>
        )}
        {!error && (
          <ListFooter
            anzahl={filtered.length}
            gesamt={gesamtInAnsicht}
            einheit={gesamtInAnsicht === 1 ? "Position" : "Positionen"}
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
            ohneSortierHinweis={narrow}
          />
        )}
      </Card>

      {/* Nachtraegliche KI-Pruefung (Rechtschreibung + Allergene) der aktuellen Karte — editierbar */}
      <Drawer open={recheckOpen} onClose={closeRecheck} title="Karte erneut prüfen (KI)" breite="breit">
        <div className="space-y-6">
          {recheckErr && <ErrorNote message={recheckErr} />}
          {recheckBusy && (
            <p className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
              <Icon name="progress_activity" className="animate-spin text-[18px]" /> Die KI prüft die Karte …
            </p>
          )}
          {!recheckBusy && recheckDone && recheckSpellRows.length === 0 && recheckAllergenRows.length === 0 && (
            <p className="rounded-lg border border-outline-variant/30 bg-surface-container-low p-4 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30 dark:bg-surface-variant/10">
              Keine Vorschläge gefunden. Du kannst Positionen jederzeit direkt in der Liste bearbeiten.
            </p>
          )}

          {/* Rechtschreibung */}
          {!recheckBusy && recheckSpellRows.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-title-sm text-title-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Rechtschreibung ({recheckSpellRows.filter((r) => r.status !== "done").length} offen)
                </h3>
                {recheckSpellRows.some((r) => r.status === "open" || r.status === "error") && (
                  <Button type="button" variant="secondary" className="!px-3 !py-1.5" onClick={() => void applyAllSpellRows()}>
                    Alle übernehmen
                  </Button>
                )}
              </div>
              {recheckSpellRows.map((r) => (
                <div key={r.key} className={`rounded-lg border p-3 ${r.status === "done" ? "border-secondary/30 bg-secondary/5" : "border-outline-variant/30 bg-surface-container-lowest dark:border-outline/30 dark:bg-surface-variant/10"}`}>
                  <span className="mb-1 inline-block rounded bg-surface-container-high px-1.5 py-0.5 font-label-md text-[11px] uppercase tracking-wide text-on-surface-variant dark:bg-white/[0.08]">
                    {r.field === "name" ? "Name" : "Beschreibung"}
                  </span>
                  <p className="font-body-sm text-[12px] text-on-surface-variant/70 line-through decoration-red-400/60">{r.original}</p>
                  {/* Editierbar: Vorschlag übernehmen ODER manuell anpassen — Feld wächst mit dem Text */}
                  <AutoTextarea
                    className="mt-1 w-full text-body-sm"
                    value={r.value}
                    disabled={r.status === "saving" || r.status === "done"}
                    onChange={(v) => setSpellRowValue(r.key, v)}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {r.status === "done" ? (
                      <span className="inline-flex items-center gap-1 font-label-md text-[12px] font-semibold text-secondary dark:text-[#b8ae9e]"><Icon name="check_circle" className="text-[16px]" /> Übernommen</span>
                    ) : (
                      <>
                        <Button type="button" variant="primary" disabled={r.status === "saving"} className="!px-3 !py-1.5 disabled:opacity-50" onClick={() => void applySpellRow(r)}>
                          <Icon name="check" className="text-[16px]" /> {r.status === "saving" ? "Speichert …" : "Übernehmen"}
                        </Button>
                        <Button type="button" variant="ghost" disabled={r.status === "saving"} className="!px-3 !py-1.5 text-on-surface-variant disabled:opacity-50" onClick={() => setRecheckSpellRows((rows) => rows.filter((x) => x.key !== r.key))}>
                          Verwerfen
                        </Button>
                      </>
                    )}
                    {r.status === "error" && r.err && <span className="font-body-sm text-[12px] text-error dark:text-[#e39b92]">{r.err}</span>}
                  </div>
                </div>
              ))}
            </section>
          )}

          {/* Allergene */}
          {!recheckBusy && (
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-title-sm text-title-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Allergene ({recheckAllergenRows.filter((r) => r.status !== "done").length} offen)
                </h3>
                {recheckAllergenRows.some((r) => r.status === "open" || r.status === "error") && (
                  <Button type="button" variant="secondary" className="!px-3 !py-1.5" onClick={() => void applyAllAllergenRows()}>
                    Alle übernehmen
                  </Button>
                )}
              </div>
              {/* Allergene erneut prüfen — optional auch bereits erfasste Positionen (Zweitmeinung) */}
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-outline-variant/20 bg-surface-container-low/60 p-2 dark:border-outline/20 dark:bg-surface-variant/10">
                <Button type="button" variant="secondary" disabled={recheckAllergenBusy} className="!px-3 !py-1.5 disabled:opacity-50" onClick={() => void rerunRecheckAllergens(recheckIncludeDeclared)}>
                  <Icon name={recheckAllergenBusy ? "progress_activity" : "auto_awesome"} className={`text-[16px] ${recheckAllergenBusy ? "animate-spin" : ""}`} /> Allergene neu prüfen
                </Button>
                <label className="inline-flex cursor-pointer items-center gap-1.5 font-body-sm text-[12px] text-on-surface-variant">
                  <Checkbox checked={recheckIncludeDeclared} onChange={(e) => void rerunRecheckAllergens(e.target.checked)} />
                  auch bereits erfasste Positionen
                </label>
              </div>
              {recheckAllergenBusy && (
                <p className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
                  <Icon name="progress_activity" className="animate-spin text-[18px]" /> Prüft Allergene …
                </p>
              )}
              {!recheckAllergenBusy && recheckAllergenRows.length === 0 && recheckDone && (
                <p className="font-body-sm text-[12px] text-on-surface-variant/70">
                  {recheckIncludeDeclared ? "Keine abweichenden Allergen-Vorschläge." : "Keine Vorschläge — alle Positionen haben bereits Allergene. Für eine Zweitmeinung oben die Option für bereits erfasste Positionen ankreuzen."}
                </p>
              )}
              {recheckAllergenRows.map((r) => (
                <div key={r.key} className={`rounded-lg border p-3 ${r.status === "done" ? "border-secondary/30 bg-secondary/5" : "border-outline-variant/30 bg-surface-container-lowest dark:border-outline/30 dark:bg-surface-variant/10"}`}>
                  <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{r.name}</p>
                  {r.current != null && (
                    <p className="mt-0.5 font-body-sm text-[12px] text-on-surface-variant/70">
                      Aktuell: {r.current.length === 0 ? "keine" : r.current.join(", ")}
                    </p>
                  )}
                  {/* Editierbar: Codes an-/abwählen (KI-Vorschlag vorausgewählt) */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {LMIV.map((l) => {
                      const on = r.codes.includes(l.code);
                      return (
                        <button
                          key={l.code}
                          type="button"
                          disabled={r.status === "saving" || r.status === "done"}
                          title={l.label}
                          onClick={() => toggleAllergenRowCode(r.key, l.code)}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-label-md text-[12px] font-semibold transition-colors disabled:opacity-60 ${on ? "bg-secondary/20 text-secondary dark:text-[#b8ae9e]" : "bg-surface-container-high text-on-surface-variant/70 dark:bg-white/[0.08]"}`}
                        >
                          {l.code}
                        </button>
                      );
                    })}
                  </div>
                  {r.reason && <p className="mt-1 font-body-sm text-[12px] text-on-surface-variant/80">{r.reason} — ersetzt keine Rechtsberatung, bitte prüfen.</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {r.status === "done" ? (
                      <span className="inline-flex items-center gap-1 font-label-md text-[12px] font-semibold text-secondary dark:text-[#b8ae9e]"><Icon name="check_circle" className="text-[16px]" /> Übernommen</span>
                    ) : (
                      <>
                        <Button type="button" variant="primary" disabled={r.status === "saving"} className="!px-3 !py-1.5 disabled:opacity-50" onClick={() => void applyAllergenRow(r)}>
                          <Icon name="check" className="text-[16px]" /> {r.status === "saving" ? "Speichert …" : r.codes.length === 0 ? "Keine übernehmen" : "Übernehmen"}
                        </Button>
                        <Button type="button" variant="ghost" disabled={r.status === "saving"} className="!px-3 !py-1.5 text-on-surface-variant disabled:opacity-50" onClick={() => setRecheckAllergenRows((rows) => rows.filter((x) => x.key !== r.key))}>
                          Verwerfen
                        </Button>
                      </>
                    )}
                    {r.status === "error" && r.err && <span className="font-body-sm text-[12px] text-error dark:text-[#e39b92]">{r.err}</span>}
                  </div>
                </div>
              ))}
            </section>
          )}

          <p className="font-body-sm text-[12px] text-on-surface-variant/70">
            Übernehmen ändert die Position direkt (gleiche ID) — Paket-Verknüpfungen bleiben erhalten. Du kannst jeden Vorschlag vor dem Übernehmen anpassen.
          </p>
        </div>
      </Drawer>

      {/* Anlegen / Bearbeiten */}
      <Drawer
        open={itemForm != null}
        onClose={() => setItemForm(null)}
        title={itemForm?.id ? "Position bearbeiten" : "Position anlegen"}
      >
        {itemForm && (
          <form className="space-y-5" onSubmit={(e) => void handleSaveItem(e)}>
            {formError && <ErrorNote message={formError} />}

            <Field label="Bezeichnung *">
              <Input
                required
                value={itemForm.name}
                onChange={(e) => setItemForm(mitPortionsgroesseVorschlag(itemForm, { name: e.target.value }))}
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Art">
                <Select
                  value={itemForm.kind}
                  onValueChange={(v) => setItemForm({ ...itemForm, kind: v as CatalogKind })}
                  options={KIND_ORDER.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
                />
              </Field>
              {isResourceKind(itemForm.kind) ? (
                <Field label="Bereich">
                  {/* Nur Anzeige (kein Formularfeld): `InputOptik` ist ein `<div>` in Feld-Optik,
                      kein Eingabeelement. */}
                  <InputOptik className="flex items-center text-on-surface-variant">
                    {KIND_LABEL[itemForm.kind]} · ohne Kategorie
                  </InputOptik>
                </Field>
              ) : cardedCategories.length === 0 ? (
                <Field label="Kategorie *">
                  <p className="rounded-lg bg-surface-container-low p-3 font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/10">
                    Keine Kategorie mit Speisekarte vorhanden. Bitte zuerst eine Karte + Kategorie anlegen.
                  </p>
                </Field>
              ) : (
                <Field label="Kategorie *">
                  <Select
                    value={itemForm.categoryId}
                    onValueChange={(v) => setItemForm({ ...itemForm, categoryId: v })}
                    placeholder="Kategorie wählen …"
                    options={cardedCategories.map((c) => {
                      const cardName = cardNameById.get(c.cardId ?? "");
                      return { value: c.id, label: cardName ? `${cardName} · ${c.name}` : c.name };
                    })}
                  />
                </Field>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Preis (Brutto €)">
                <Input
                  inputMode="decimal"
                  placeholder="0,00"
                  value={itemForm.priceEuros}
                  onChange={(e) => setItemForm({ ...itemForm, priceEuros: sanitizePriceInput(e.target.value) })}
                />
              </Field>
              <Field label="Einheit">
                <Select
                  value={itemForm.pricingUnit}
                  onValueChange={(v) => setItemForm({ ...itemForm, pricingUnit: v as PricingUnit, pricingUnitTouched: true })}
                  options={(Object.keys(PRICING_UNIT_LABEL) as PricingUnit[]).map((u) => ({ value: u, label: PRICING_UNIT_LABEL[u] }))}
                />
              </Field>
            </div>

            <Field label="MwSt-Klasse">
              <Select
                value={itemForm.vatClass}
                onValueChange={(v) => setItemForm({ ...itemForm, vatClass: v as VatClass })}
                options={(Object.keys(VAT_LABEL) as VatClass[]).map((v) => ({ value: v, label: VAT_LABEL[v] }))}
              />
            </Field>

            <Field label="Preis-Hinweis (optional, rechnet nie)">
              <Input
                placeholder='z. B. "ab 12,90 €"'
                value={itemForm.priceNote}
                onChange={(e) => setItemForm({ ...itemForm, priceNote: e.target.value })}
              />
            </Field>

            <Field label="Beschreibung">
              <Textarea
                className="min-h-[80px]"
                value={itemForm.description}
                onChange={(e) => setItemForm(mitPortionsgroesseVorschlag(itemForm, { description: e.target.value }))}
              />
            </Field>

            {/* Allergene (LMIV) */}
            <div>
              <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Allergene (LMIV)
              </span>
              <p className="mb-2 font-body-sm text-body-sm text-on-surface-variant/70">
                Nichts auswählen = <em>ungepflegt</em>. Bewusst „keine" via Häkchen unten.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {LMIV.map(({ code, label }) => {
                  const on = (itemForm.allergens ?? []).includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      title={label}
                      onClick={() => {
                        // Auswahl eines Allergens macht aus „ungepflegt" (null) einen gepflegten Stand.
                        const cur = itemForm.allergens ?? [];
                        setItemForm({
                          ...itemForm,
                          allergens: on ? cur.filter((a) => a !== code) : [...cur, code],
                        });
                      }}
                      className={`rounded-lg border px-2.5 py-1 font-label-md text-label-md transition-colors ${
                        on
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-outline-variant/50 text-on-surface-variant hover:border-primary/40 dark:border-outline/40 dark:text-secondary-fixed-dim"
                      }`}
                    >
                      <span className="font-bold">{code}</span> {label}
                    </button>
                  );
                })}
              </div>
              <label className="mt-2 flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                <Checkbox
                  // Tri-State: angehakt = [] (geprüft: keine), abgehakt = null (ungepflegt).
                  checked={itemForm.allergens != null && itemForm.allergens.length === 0}
                  onChange={(e) => setItemForm({ ...itemForm, allergens: e.target.checked ? [] : null })}
                />
                Geprüft: enthält keine der 14 Hauptallergene
              </label>
              {/* H-02-Rest (Migration 336): Alt-Import-Bestand mit []-Default, nie einzeln
                  bestätigt — die Checkbox oben zeigt zwar bereits "angehakt" (Wert ist ja []),
                  ist das aber nicht zwangsläufig durch eine echte Prüfung. Eigene, unübersehbare
                  Aktion statt stillschweigend beim nächsten Speichern "gratis" mitzuziehen
                  (s. handleSaveItem, allergensChanged-Guard). */}
              {itemForm.allergensLoaded != null && itemForm.allergensLoaded.length === 0 && itemForm.allergensCheckedAt == null && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-secondary/10 px-3 py-2 font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">
                  <Icon name="warning" className="text-[16px]" />
                  <span className="flex-1">
                    Ungeprüfter Alt-Bestand aus einem Import vor der Einzel-Bestätigung — die Küche und der Katalog zeigen diese Position
                    weiter als „ungeklärt", bis sie hier explizit bestätigt wird.
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    className="!py-1"
                    disabled={busy}
                    onClick={async () => {
                      if (!itemForm.id) return;
                      setBusy(true);
                      setFormError(null);
                      try {
                        await apiMutate(`/api/catalog/items/${itemForm.id}`, "PATCH", { allergens: [] });
                        setItemForm({ ...itemForm, allergens: [], allergensLoaded: [], allergensCheckedAt: new Date().toISOString() });
                        itemsQ.reload();
                      } catch {
                        setFormError("Bestätigen fehlgeschlagen — bitte erneut versuchen.");
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Jetzt bestätigen
                  </Button>
                </div>
              )}
            </div>

            {/* Ernährung */}
            <div>
              <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                Ernährung
              </span>
              <div className="flex flex-wrap gap-1.5">
                {DIETARY.map(({ key, label }) => {
                  const on = !!itemForm.dietary[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setItemForm({ ...itemForm, dietary: { ...itemForm.dietary, [key]: !on } })}
                      className={`rounded-lg border px-2.5 py-1 font-label-md text-label-md transition-colors ${
                        on
                          ? "border-outline bg-secondary/10 text-secondary dark:text-[#b8ae9e]"
                          : "border-outline-variant/50 text-on-surface-variant hover:border-secondary/40 dark:border-outline/40 dark:text-secondary-fixed-dim"
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mehrsprachigkeit: Übersetzungen je Sprache (Angebots-Sprache steuert die Anzeige
                beim Kunden; Basissprache bleibt das Namensfeld oben). */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Übersetzungen
                </span>
                <Select
                  ariaLabel="Sprache der Übersetzung"
                  className="!h-8 !w-auto !py-0.5 text-body-sm"
                  value={editLang}
                  onValueChange={setEditLang}
                  options={[
                    { value: "en", label: "Englisch" },
                    { value: "it", label: "Italienisch" },
                    { value: "fr", label: "Französisch" },
                    { value: "es", label: "Spanisch" },
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Input
                  placeholder={`Name (${editLang.toUpperCase()}) – leer = keine Übersetzung`}
                  value={itemForm.translations[editLang]?.name ?? ""}
                  onChange={(e) =>
                    setItemForm({
                      ...itemForm,
                      translations: { ...itemForm.translations, [editLang]: { ...itemForm.translations[editLang], name: e.target.value } },
                    })
                  }
                />
                <Input
                  placeholder={`Beschreibung (${editLang.toUpperCase()}), optional`}
                  value={itemForm.translations[editLang]?.description ?? ""}
                  onChange={(e) =>
                    setItemForm({
                      ...itemForm,
                      translations: {
                        ...itemForm.translations,
                        [editLang]: { ...itemForm.translations[editLang], description: e.target.value || null },
                      },
                    })
                  }
                />
              </div>
            </div>

            {itemForm.id && (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-low px-3 py-2 font-body-sm text-body-sm text-on-surface-variant dark:border-outline/30 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
                <Checkbox
                  checked={itemForm.archived}
                  onChange={(e) => setItemForm({ ...itemForm, archived: e.target.checked })}
                />
                Archiviert (nicht mehr in neuen Angeboten wählbar)
              </label>
            )}

            <div className="flex flex-wrap justify-end gap-3 pt-1">
              {/* Punkt 129: auch hier, weil die Zeilen-Knöpfe nur beim Überfahren erscheinen (Handy). */}
              {itemForm.id && (
                <Button type="button" variant="secondary" className="mr-auto" disabled={busy} onClick={() => void duplicateItem(itemForm.id!)}>
                  <Icon name="content_copy" className="text-[18px]" /> Duplizieren
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={() => setItemForm(null)}>
                Abbrechen
              </Button>
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? "Speichert …" : itemForm.id ? "Speichern" : "Position anlegen"}
              </Button>
            </div>
          </form>
        )}
      </Drawer>

      {/* Kategorie anlegen */}
      <Drawer open={catDrawerOpen} onClose={() => setCatDrawerOpen(false)} title="Kategorie anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreateCategory(e)}>
          {formError && catDrawerOpen && <ErrorNote message={formError} />}
          <Field label="Name *">
            <Input name="name" required placeholder="z. B. Antipasti, Fingerfood, Getränke" />
          </Field>
          <Field label="Speisekarte *">
            {cards.length > 0 ? (
              <Select
                value={catCardId}
                onValueChange={setCatCardId}
                placeholder="Karte wählen …"
                options={cards.map((card) => ({ value: card.id, label: card.name }))}
              />
            ) : (
              <p className="rounded-lg bg-surface-container-low p-3 font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-variant/10">
                Noch keine Karte vorhanden. Legen Sie zuerst eine Karte an (Karte importieren) – Kategorien gehören immer zu einer Karte.
              </p>
            )}
          </Field>
          <Field label="Nur für Art (optional)">
            <Select
              value={catKindScope}
              onValueChange={setCatKindScope}
              options={[
                { value: KEIN_WERT, label: "Alle Arten" },
                ...KIND_ORDER.map((k) => ({ value: k, label: KIND_LABEL[k] })),
              ]}
            />
          </Field>
          {activeCategories.length > 0 && (
            <div className="rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
              <p className="mb-2 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Vorhandene Kategorien
              </p>
              <div className="flex flex-wrap gap-1.5">
                {activeCategories.map((c) => (
                  <span
                    key={c.id}
                    className="rounded-lg bg-surface-container-high px-2 py-1 font-label-md text-label-md text-on-surface-variant dark:bg-white/[0.08]"
                  >
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="secondary" onClick={() => setCatDrawerOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={busy || cards.length === 0}>
              {busy ? "Speichert …" : "Kategorie anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>
    </>
  );
  }
}

/* ------------------------------- Kleinteile -------------------------------- */
function FilterChip({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-label-md text-label-md transition-colors ${
        active
          ? "bg-primary text-on-primary"
          : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-white/[0.08] dark:text-secondary-fixed-dim"
      }`}
    >
      {icon && <Icon name={icon} className="text-[16px]" />}
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // min-w-0: `Field` ist in mehreren Grids (Art/Bereich, Preis/Einheit) direktes Grid-Item — s.
  // VeranstaltungsDatenDrawer.tsx fuer die Herleitung. In Block-Kontext wirkungslos, darum hier
  // zentral statt an jedem Aufrufer.
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
        {label}
      </span>
      {children}
    </label>
  );
}

function errorText(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("duplicate_name")) return "Es gibt bereits eine aktive Position mit diesem Namen in dieser Kategorie.";
  if (msg.includes("invalid_category")) return "Die gewählte Kategorie ist ungültig.";
  if (msg.includes("category_required")) return "Speisen und Getränke brauchen eine Kategorie, die zu einer Speisekarte gehört.";
  if (msg.includes("category_needs_card")) return "Die gewählte Kategorie gehört zu keiner Karte.";
  /*
   * Punkt 81 (21.09.2026): Diesen Fehler kann der Server seit der Equipment-Karte GAR NICHT MEHR
   * senden — Equipment, Personal und Sonstiges DÜRFEN jetzt in eine Karte. Der Eintrag bleibt
   * trotzdem stehen: Eine Oberfläche, die einen alten Fehlercode nicht mehr kennt, zeigt dem
   * Betrieb den nackten technischen Text. Solange nicht sicher ist, dass kein Altstand mehr
   * antwortet, ist ein verständlicher Satz besser als keiner — und der Satz sagt jetzt die
   * Wahrheit statt der alten Regel.
   */
  if (msg.includes("resource_has_category")) return "Diese Position kann dieser Kategorie nicht zugeordnet werden.";
  if (msg.includes("card_not_found")) return "Die gewählte Speisekarte existiert nicht (oder ist archiviert).";
  if (msg.includes("category_in_use")) return "Diese Kategorie enthält aktive Speisen/Getränke – bitte zuerst umhängen oder archivieren.";
  if (msg.includes("forbidden_role")) return "Nur Inhaber dürfen das.";
  return msg;
}

/** Sortierung als Menü für die Handy-Karten (Spaltenköpfe gibt es dort nicht). Schlüssel wie `spalten`. */
const KATALOG_SORTIERUNG = [
  { label: "Standard", key: null },
  { label: "A–Z", key: "position", dir: "asc" },
  { label: "Z–A", key: "position", dir: "desc" },
  { label: "Kategorie", key: "kategorie", dir: "asc" },
  { label: "Preis ↑", key: "preis", dir: "asc" },
  { label: "Preis ↓", key: "preis", dir: "desc" },
] as const;

/** Ein Allergen-Kürzel (LMIV-Code), der Name steht im Tooltip und für Screenreader. */
function AllergenKuerzel({ code }: { code: string }) {
  const name = LMIV.find((l) => l.code === code)?.label ?? code;
  return (
    <span
      className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-surface-container-high px-1 font-label-md text-[11px] font-semibold text-on-surface-variant dark:bg-white/[0.08]"
      title={name}
      aria-label={name}
    >
      {code}
    </span>
  );
}

const KARTE_MAX_ALLERGENE = 4;

/**
 * Handy-Mockup 26.09.2026 (Entscheidung 7, Frage 2): jede Position eine Karte — Name in voller
 * Breite, darunter Kategorie und Allergen-Kürzel (höchstens vier, dann „+N“), rechts Preis mit
 * Einheit. „Ungeprüft“ bleibt als gelbe Warnung sichtbar. Karten-Gruppen wie in der Tabelle.
 */
function KatalogKarten({
  gruppen,
  loading,
  categoryName,
  onOpen,
}: {
  gruppen: { key: string; label: string | null; items: CatalogItem[] }[];
  loading: boolean;
  categoryName: (id: string | null) => string;
  onOpen: (it: CatalogItem) => void;
}) {
  if (loading) return <LoadingRows rows={6} />;
  return (
    <div data-testid="katalog-karten">
      {gruppen.map((g) => (
        <section key={g.key}>
          {g.label != null && (
            <h3 data-testid="katalog-gruppe" className="flex items-center gap-2 border-t border-[#F6F0E4] bg-[#FBF8F1]/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-on-surface dark:border-white/[0.06] dark:bg-white/[0.03] dark:text-inverse-on-surface">
              <Icon name="menu_book" className="text-[15px] text-primary" /> {g.label}
              <span className="font-normal normal-case tracking-normal text-on-surface-variant/70">· {g.items.length} Position{g.items.length === 1 ? "" : "en"}</span>
            </h3>
          )}
          <ul>
            {g.items.map((it) => {
              const offen = allergeneOffen(it);
              const codes = it.allergens ?? [];
              return (
                <li key={it.id} className="border-t border-[#F6F0E4] dark:border-white/[0.06]">
                  <button
                    type="button"
                    data-testid={`katalog-karte-${it.id}`}
                    onClick={() => onOpen(it)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03] ${it.archivedAt != null ? "opacity-55" : ""}`}
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary-container text-on-secondary-container dark:bg-surface-variant dark:text-inverse-on-surface">
                      <Icon name={KIND_ICON[it.kind]} className="text-[17px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-semibold leading-snug text-on-background dark:text-inverse-on-surface">
                        {it.name}
                        {it.archivedAt != null && <span className="ml-1.5 text-[10px] font-medium uppercase text-on-surface-variant">Archiviert</span>}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                        <span className="mr-1">{it.categoryId ? categoryName(it.categoryId) : "Ohne Kategorie"}</span>
                        {offen ? (
                          <span className="inline-flex items-center gap-0.5 rounded bg-secondary/15 px-1.5 py-0.5 text-[10.5px] font-semibold text-secondary dark:text-[#b8ae9e]">
                            <Icon name="warning" className="text-[13px]" /> Allergene ungeprüft
                          </span>
                        ) : codes.length === 0 ? (
                          <span className="text-secondary dark:text-[#b8ae9e]">keine Allergene</span>
                        ) : (
                          <>
                            {codes.slice(0, KARTE_MAX_ALLERGENE).map((a) => (
                              <AllergenKuerzel key={a} code={a} />
                            ))}
                            {codes.length > KARTE_MAX_ALLERGENE && (
                              <span
                                className="text-[11px] font-semibold text-on-surface-variant"
                                title={codes.slice(KARTE_MAX_ALLERGENE).map((c) => LMIV.find((l) => l.code === c)?.label ?? c).join(", ")}
                              >
                                +{codes.length - KARTE_MAX_ALLERGENE}
                              </span>
                            )}
                          </>
                        )}
                      </span>
                    </span>
                    <span className="max-w-[120px] shrink-0 text-right">
                      <span className="block text-[13px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">
                        {it.priceNote ? it.priceNote : money(it.priceCents)}
                      </span>
                      <span className="block text-[11px] text-secondary dark:text-secondary-fixed-dim">{PRICING_UNIT_LABEL[it.pricingUnit]}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
